// The shapes BidSheet's props take, as validators.
//
// The argument is bidCardShapes.js's and is not restated here: `type: Object`
// says an object arrived and cannot say WHICH MEMBERS, and that gap fails
// silently. What is different is the stake. A card rendering a blank badge is
// one wrong row in a lane of sixty; this component is where a dealer commits
// money, and a roster entry missing its `id` renders a chip that changes who
// the bid is committed under and says nothing while it does it.
//
// The two shapes the tray shares with the card — `headline` and `disabled` —
// are IMPORTED rather than re-declared. They are the same fact in the same
// members on two surfaces, and a second copy is how the two drift.

import { isAbsentOrNonBlankString, isNonBlankString } from './bidCardShapes';

export {
  isAbsentOrNonBlankString,
  isNonBlankString,
  isValidDisabled,
  isValidHeadline,
} from './bidCardShapes';

/**
 * The multiples of an asset's own increment the tray offers, as chips.
 *
 * Keyed by the multiplier rather than by an absolute amount so a selection
 * survives moving to an asset with a different increment — `+$200` on a
 * $100-increment asset and `+$500` on a $250 one are the same choice, which is
 * "two increments", and that is what the model carries.
 */
export const BID_INCREMENT_MULTIPLIERS = Object.freeze([1, 2, 4, 8]);

/**
 * The wholesale standard ladder: $100 × [1, 2, 4, 8].
 *
 * A REAL default rather than an empty one, and the difference is the control
 * that commits money. Every money figure in the tray reads the `multiplier: 1`
 * entry, so `default: () => []` renders `$NaN` on the bid button through a
 * legal prop value rather than through a coding slip.
 *
 * $100 is the wholesale standard, and naming it here keeps the fallback on the
 * contract surface rather than inside a helper, where it was an `||` a reader
 * could not see without opening the template.
 *
 * It is the default for a caller that OMITS the prop, and nothing more. The
 * layers below no longer invent it: the runtime boundary answers `null` for an
 * asset the engine has not given an increment to, and the app passes that
 * `null` through, because a raise built on an invented increment is one the
 * engine refuses. A ladder is the default; a dash is an answer.
 */
export const STANDARD_BID_INCREMENTS = Object.freeze(
  BID_INCREMENT_MULTIPLIERS.map((multiplier) => Object.freeze({ multiplier, amount: multiplier * 100 }))
);

/** Whole dollars, or absent. Bidding never produces cents. */
export const isAbsentOrWholeDollars = (value) => value === null || Number.isInteger(value);

/**
 * A plain object carrying exactly `required` and nothing else.
 *
 * Closed in both directions, unlike the card's `isClosedObject`: these are list
 * ELEMENTS rather than optional member bags, so a missing member is as much a
 * mistake as an unknown one. An increment with no `amount` renders a chip with
 * no figure on it; a dealership with no `id` renders a chip that cannot be
 * matched against `biddingAsId` and so never reads as selected.
 */
function hasExactly(value, required) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length === required.length && required.every((key) => keys.includes(key));
}

/**
 * `[{ multiplier, amount }]` — the increment ladder, the store's own shape.
 *
 * NON-EMPTY, and this is the load-bearing half. Every money figure in the tray
 * reads the `multiplier: 1` entry — the bid button is
 * `price + selectedMultiplier × increment` and the proxy floor is
 * `price + increment` — so an empty array makes both `undefined` and renders
 * `$NaN` on the control that commits money. A contract that can be satisfied
 * into a `NaN` is not guarding anything.
 *
 * The `multiplier: 1` entry must be PRESENT for the same reason: it is this
 * asset's real increment and the arithmetic is built on it, so a ladder that
 * starts at 2 is the same failure wearing a legal-looking shape.
 *
 * `null` is NOT a ladder and does not pass here — it is the separate answer
 * "this asset has no increment", which the engine gives every asset it has not
 * started yet. BidSheet's own prop validator accepts it beside a valid ladder;
 * this function stays the test for a LADDER, so an empty array is still the
 * failure it has always been.
 */
export function isValidIncrements(increments) {
  if (!Array.isArray(increments) || increments.length === 0) return false;
  if (!increments.every((entry) => hasExactly(entry, ['multiplier', 'amount']))) return false;
  if (!increments.every((entry) => Number.isInteger(entry.multiplier) && entry.multiplier > 0)) return false;
  if (!increments.every((entry) => Number.isInteger(entry.amount) && entry.amount > 0)) return false;
  return increments.some((entry) => entry.multiplier === 1);
}

/**
 * `[{ id, name, role }]` — the dealerships this dealer may bid as.
 *
 * REQUIRED, and EMPTY IS A STATE. This read the other way until the roster
 * stopped being a constant in the app: it is a server record now, fetched
 * alongside the runtime token, so it is empty until that call resolves and a
 * tray can be opened inside that window. It is also empty, honestly, for a
 * dealer nobody has issued a badge to. The tray answers by leaving the chip
 * row out rather than by drawing a row with nothing in it.
 *
 * Still required rather than `default: () => []`, because the distinction that
 * matters is between "no dealerships" and "nobody passed the roster", and a
 * default erases it.
 *
 * `role` is displayed data — `Primary` or `Rep` — and is rendered beside the
 * account number, because the account is what disambiguates two dealerships
 * sharing a trading name. It is OPTIONAL, matching the record: the key must be
 * present so a typo in it is still caught, and `null` is how it says the dealer
 * stands at that dealership in no stated capacity.
 */
export function isValidDealershipRoster(roster) {
  if (!Array.isArray(roster)) return false;
  return roster.every(
    (entry) =>
      hasExactly(entry, ['id', 'name', 'role'])
      && isNonBlankString(entry.id)
      && isNonBlankString(entry.name)
      && isAbsentOrNonBlankString(entry.role)
  );
}
